import { useState } from "react";
import { MessageCircle, Send, MapPin, Clock, Star, ArrowRight, Mail } from "lucide-react";
import { useWhatsApp } from "@/hooks/useWhatsApp";
import { BRAND } from "@/lib/constants/futurestation";
import { Button } from "@/components/ui/button";
import ContactFormModal from "@/components/ui/contact-form-modal";
import Container from "@/components/ui/container";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

const ContactSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { openWhatsApp } = useWhatsApp();

  const stats = [
    { icon: Star, label: "Anos de experiência", value: "10+" },
    { icon: MapPin, label: "Capitais atendidas", value: "4" },
    { icon: Clock, label: "Horas de expertise", value: "300k+" }
  ];

  return (
    <section id="contact" className="py-24 bg-background dark:bg-fs-dark-950 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-fs-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-fs-cyan-500/10 rounded-full blur-3xl" />
      </div>

      <Container className="relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-4xl mx-auto"
        >
          <div>
            <div className="space-y-8">
              {/* Badge */}
              <div className="inline-flex items-center px-4 py-2 bg-card/80 backdrop-blur-sm rounded-full text-foreground text-sm font-medium border border-border">
                <Star className="w-4 h-4 mr-2" />
                100% especializada em Fluig
              </div>

              {/* Heading */}
              <div className="space-y-4">
                <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-foreground dark:text-white leading-tight">
                  Pronto para{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-fs-purple-500 to-fs-cyan-500">
                    transformar
                  </span>{" "}
                  seus processos?
                </h2>
                <p className="text-xl lg:text-2xl text-muted-foreground dark:text-white/70 font-light">
                  Seu Fluig já tem tudo o que você precisa. Falta só ativar o potencial dele!
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 py-8">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="flex justify-center mb-3">
                      <stat.icon className="w-8 h-8 text-fs-purple-500" />
                    </div>
                    <div className="text-2xl lg:text-3xl font-bold text-foreground dark:text-white mb-1">
                      {stat.value}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="pt-6">
                <Button
                  onClick={() => setIsModalOpen(true)}
                  size="default"
                  variant="fs-primary"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Quero fazer meu Fluig decolar agora!
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </Container>

      {/* Contact Form Modal */}
      <ContactFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        triggerContext="contact"
      />
    </section>
  );
};

export default ContactSection;